import React from 'react';
import { motion } from 'framer-motion';

/**
 * NewModulesShowcase
 * Highlights all the new holistic modules added beyond the original
 * calorie-counter scope: fasting, digestion, mindfulness, medicinal
 * plants, AlphaFold molecular explorer, Cocina Inteligente, and the
 * seasonal weekly planner.
 *
 * Each card uses an inline animated SVG so the page stays light (no
 * Lottie JSON downloads, no extra dependencies) while still feeling
 * alive on scroll-in.
 */

const MODULES = [
  {
    id: 'plantas-medicinales',
    title: 'Plantas medicinales mexicanas',
    blurb:
      'Catálogo basado en el Atlas UNAM. Cada planta con sus dianas moleculares y recetas curadas paso a paso.',
    accent: '#1F5C3D',
    bg: 'from-[#E2EBDD] to-[#F4EBD9]',
    icon: <PlantIcon />,
  },
  {
    id: 'alphafold',
    title: 'Estructura molecular 3D',
    blurb:
      'Visualiza los compuestos activos en 3D usando AlphaFold de DeepMind. Mira por qué cada planta funciona.',
    accent: '#2C7D8C',
    bg: 'from-[#D9E8EA] to-[#F4EBD9]',
    icon: <MoleculeIcon />,
  },
  {
    id: 'cocina-inteligente',
    title: 'Cocina inteligente',
    blurb:
      'Técnica + nutrición + sabor. Aprende a cocinar verduras con criterio, paso a paso.',
    accent: '#E0892E',
    bg: 'from-[#F6E6CC] to-[#F4EBD9]',
    icon: <CookingIcon />,
  },
  {
    id: 'planificador-estacional',
    title: 'Mi semana estacional',
    blurb:
      'Plan de 7 días con bases del domingo + producto en temporada. Respeta tus combinaciones digestivas.',
    accent: '#C9A227',
    bg: 'from-[#F6E6CC] to-[#F4EBD9]',
    icon: <CalendarIcon />,
  },
  {
    id: 'ayuno',
    title: 'Ayuno intermitente',
    blurb:
      'Cronómetro y diario de ayunos. Métricas de constancia, racha y duración promedio — sin culpa.',
    accent: '#B8531F',
    bg: 'from-[#F6E6CC] to-[#F4EBD9]',
    icon: <ClockIcon />,
  },
  {
    id: 'digestion',
    title: 'Diario de digestión',
    blurb:
      'Registra cómo te sientes después de comer. Patrones que te ayudan a entender tu cuerpo.',
    accent: '#8E2C42',
    bg: 'from-[#F1DBDF] to-[#F4EBD9]',
    icon: <DigestionIcon />,
  },
  {
    id: 'mindfulness',
    title: 'Mindfulness alimentario',
    blurb:
      'Práctica STOP, escala de hambre real vs emocional. Vuelve a escuchar a tu cuerpo.',
    accent: '#5E7A5E',
    bg: 'from-[#E2EBDD] to-[#F4EBD9]',
    icon: <MindfulnessIcon />,
  },
];

const NewModulesShowcase = () => (
  <section
    id="modulos-holisticos"
    className="cdx-grain relative"
    style={{ padding: '88px 0', background: 'var(--cdx-paper)', fontFamily: 'var(--cdx-font-body)' }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="text-center mb-14"
      >
        <span className="cdx-kicker">Sistema integral de salud</span>
        <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', margin: '10px 0 12px', color: 'var(--cdx-ink)' }}>
          Mucho más que <em style={{ color: 'var(--cdx-jade)', fontStyle: 'italic' }}>contar calorías</em>
        </h2>
        <p style={{ fontSize: 18, color: 'var(--cdx-ink-soft)', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
          Un sistema que une ciencia, sabiduría mexicana y técnica culinaria.
          Esto es lo que ya puedes usar:
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {MODULES.map((module, idx) => (
          <motion.article
            key={module.id}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: idx * 0.06 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className={`relative bg-gradient-to-br ${module.bg} rounded-2xl p-6 overflow-hidden`}
            style={{ border: '1px solid var(--cdx-paper-edge)', boxShadow: 'var(--cdx-shadow-sm)' }}
          >
            <div
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10"
              style={{ background: module.accent }}
            />
            <div className="relative">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                style={{ background: `${module.accent}22` }}
              >
                {React.cloneElement(module.icon, { color: module.accent })}
              </div>
              <h3 className="cdx-display" style={{ fontSize: 17, fontWeight: 600, color: 'var(--cdx-ink)', marginBottom: 8 }}>{module.title}</h3>
              <p style={{ fontSize: 13.5, color: 'var(--cdx-ink-soft)', lineHeight: 1.55 }}>{module.blurb}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------------------- Animated icons ----------------------------- */

const wrap = (children) => (
  <svg viewBox="0 0 48 48" width="32" height="32" fill="none">{children}</svg>
);

function PlantIcon({ color = '#84CC16' }) {
  return wrap(
    <g>
      <path d="M 24 38 L 24 20" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
      <motion.path
        d="M 24 26 Q 16 22 14 14 Q 22 16 24 24 Z"
        fill={color}
        animate={{ rotate: [0, 4, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <motion.path
        d="M 24 22 Q 32 18 34 10 Q 26 12 24 20 Z"
        fill={color}
        animate={{ rotate: [0, -4, 0] }}
        transition={{ duration: 3, repeat: Infinity, delay: 0.6 }}
        style={{ transformOrigin: '24px 22px' }}
      />
      <ellipse cx="24" cy="40" rx="6" ry="1.4" fill="rgba(0,0,0,0.15)" />
    </g>,
  );
}

function MoleculeIcon({ color = '#2C7D8C' }) {
  return wrap(
    <g>
      <line x1="14" y1="14" x2="24" y2="24" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <line x1="24" y1="24" x2="34" y2="14" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <line x1="24" y1="24" x2="24" y2="36" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <motion.circle
        cx="14" cy="14" r="4" fill={color}
        animate={{ y: [0, -2, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <motion.circle
        cx="34" cy="14" r="4" fill={color}
        animate={{ y: [0, -2, 0] }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
      />
      <motion.circle
        cx="24" cy="24" r="5" fill={color}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <motion.circle
        cx="24" cy="36" r="4" fill={color}
        animate={{ y: [0, 2, 0] }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.9 }}
      />
    </g>,
  );
}

function CookingIcon({ color = '#F59E0B' }) {
  return wrap(
    <g>
      <path d="M 10 28 L 38 28 L 36 38 L 12 38 Z" stroke={color} strokeWidth="2" fill={`${color}22`} />
      <line x1="38" y1="28" x2="44" y2="28" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <motion.path
        d="M 18 22 Q 18 14 22 16 Q 22 12 26 14 Q 28 10 30 14"
        stroke={color} strokeWidth="2" fill="none" strokeLinecap="round"
        animate={{ y: [0, -2, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </g>,
  );
}

function CalendarIcon({ color = '#14B8A6' }) {
  return wrap(
    <g>
      <rect x="8" y="12" width="32" height="28" rx="3" stroke={color} strokeWidth="2" fill={`${color}22`} />
      <line x1="8" y1="20" x2="40" y2="20" stroke={color} strokeWidth="2" />
      <line x1="16" y1="8" x2="16" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="32" y1="8" x2="32" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <motion.circle
        cx="24" cy="30" r="3" fill={color}
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 1.8, repeat: Infinity }}
        style={{ transformOrigin: '24px 30px' }}
      />
    </g>,
  );
}

function ClockIcon({ color = '#0EA5E9' }) {
  return wrap(
    <g>
      <circle cx="24" cy="24" r="14" stroke={color} strokeWidth="2" fill={`${color}22`} />
      <motion.line
        x1="24" y1="24" x2="24" y2="14"
        stroke={color} strokeWidth="2" strokeLinecap="round"
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <motion.line
        x1="24" y1="24" x2="32" y2="24"
        stroke={color} strokeWidth="2" strokeLinecap="round"
        animate={{ rotate: 360 }}
        transition={{ duration: 96, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <circle cx="24" cy="24" r="1.6" fill={color} />
    </g>,
  );
}

function DigestionIcon({ color = '#EF4444' }) {
  return wrap(
    <g>
      <motion.path
        d="M 16 12 Q 20 16 18 22 Q 14 26 18 30 Q 22 34 18 38"
        stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round"
        animate={{ pathLength: [0, 1, 1, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.path
        d="M 30 12 Q 26 18 30 22 Q 34 28 30 32 Q 26 36 30 40"
        stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round"
        animate={{ pathLength: [0, 1, 1, 0] }}
        transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
      />
    </g>,
  );
}

function MindfulnessIcon({ color = '#5E7A5E' }) {
  return wrap(
    <g>
      <motion.circle
        cx="24" cy="24" r="6"
        stroke={color} strokeWidth="2" fill={`${color}22`}
        animate={{ scale: [1, 1.4, 1], opacity: [1, 0.7, 1] }}
        transition={{ duration: 3, repeat: Infinity }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <motion.circle
        cx="24" cy="24" r="12"
        stroke={color} strokeWidth="1.5" fill="none" opacity="0.5"
        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.1, 0.5] }}
        transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
        style={{ transformOrigin: '24px 24px' }}
      />
      <motion.circle
        cx="24" cy="24" r="18"
        stroke={color} strokeWidth="1" fill="none" opacity="0.3"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, delay: 1 }}
        style={{ transformOrigin: '24px 24px' }}
      />
    </g>,
  );
}

export default NewModulesShowcase;
